import { useRef, useState } from 'react'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { api } from '@/shared/lib/api'

import {
  checkInErrorKeyFor,
  PROGRAMMING_RESOURCE_TYPE,
  type CheckInErrorKey
} from '../model/checkIn'
import { canCheckInForSession } from '../model/temporal'

import type { Session } from '../model/types'

export type CheckInStatus = '' | 'loading' | 'success'

type UseScheduleCheckInProps = {
  onFinished?: () => void
}

/**
 * Conclui o check-in de uma sessão com a palavra anunciada no palco.
 *
 * Não há leitura de QR: o participante digita, e a comparação — tolerante a
 * acento, caixa e espaço — é do servidor. A palavra nunca desce para o app,
 * então não existe nada para comparar aqui; o valor sobe como foi digitado.
 *
 * O envio é protegido por `inFlightRef`, não pelo `status`: o pico desta tela é
 * o minuto do anúncio, com milhares de toques ao mesmo tempo, e dois toques no
 * mesmo tick de render leriam o mesmo `status` antigo. O ref fecha a janela sem
 * depender de re-render, e não há retry automático — repetir a rajada por conta
 * própria multiplicaria justamente o pico.
 */
export const useScheduleCheckIn = ({
  onFinished
}: UseScheduleCheckInProps = {}) => {
  const [status, setStatus] = useState<CheckInStatus>('')
  const [errorKey, setErrorKey] = useState<CheckInErrorKey | null>(null)
  const inFlightRef = useRef(false)

  const { t } = useTranslation()
  const { digitalProductId } = useDigitalProductContext()

  const submit = async (session: Session, answer: string) => {
    if (inFlightRef.current) return false
    if (!session || !canCheckInForSession(session)) return false
    if (!answer.trim()) return false

    inFlightRef.current = true
    setStatus('loading')
    setErrorKey(null)

    try {
      await api.post(
        `/user/digital_products/${digitalProductId}/progress/finish_resource`,
        {
          record: {
            resource_id: session.id,
            resource_type: PROGRAMMING_RESOURCE_TYPE,
            answer
          }
        }
      )

      setStatus('success')
      onFinished?.()

      return true
    } catch (error) {
      setErrorKey(checkInErrorKeyFor(error))
      setStatus('')

      return false
    } finally {
      inFlightRef.current = false
    }
  }

  return {
    status,
    errorKey,
    errorMessage: errorKey ? t(errorKey) : null,
    submit
  }
}
